import { useState, useEffect, useRef } from 'react'
import appUserService from '../services/appUserService'
import { PARAM_PHONE_NUMBER, PARAM_USERNAME_PREFIX } from '../constants/params'
import { validatePhoneNumber } from '../helper/common'
import { SessionStorageManager } from '../helper/localStorage'
import { getUrlParamValue } from '../helper/params'

// Storage key cho user session
export const USER_SESSION_STORAGE_KEY = 'bhxm:notification:user_session:v1'
export const DEFAULT_PASSWORD = '123456'
export const getUsernamePrefix = () =>
  SessionStorageManager.getItem(PARAM_USERNAME_PREFIX) || getUrlParamValue(PARAM_USERNAME_PREFIX) || process.env.REACT_APP_USERNAME_PREFIX || 'TAMOVE'

// Xóa bỏ đọc phoneNumber từ sessionStorage

// Hàm map vehicleType sang vehicleSubType
function mapVehicleTypeToSubType(vehicleType) {
  const vt = Number(vehicleType)
  if (vt === 1) return 1 // Ô tô
  if (vt === 40) return 32 // Xe máy điện
  return 30 // Xe máy
}

// Hàm load user session từ localStorage
function loadUserSession() {
  try {
    const obj = JSON.parse(window.localStorage.getItem(USER_SESSION_STORAGE_KEY) || 'null')
    if (obj && obj.token && obj.appUserId) {
      return obj
    }
  } catch (error) {
    console.warn('Failed to parse user session:', error)
  }
  return null
}

// Hàm lưu user session vào localStorage
function saveUserSession(session) {
  window.localStorage.setItem(USER_SESSION_STORAGE_KEY, JSON.stringify(session))
}

// Hàm đảm bảo session user dựa trên phoneNumber
const ensureUserSessionByPhone = async (phoneNumberDigits) => {
  if (!phoneNumberDigits) return null

  // Validate phone number using helper function
  const validation = validatePhoneNumber(phoneNumberDigits)
  if (!validation.isValid) return null

  // Check cached session first
  const cached = loadUserSession()
  const username = `${getUsernamePrefix()}${phoneNumberDigits}`
  if (cached && cached.username === username) return cached

  const password = DEFAULT_PASSWORD

  const buildSessionFromLoginResult = (loginResult) => {
    if (!loginResult || loginResult.statusCode !== 200 || !loginResult.data) return null

    const token = loginResult.data.token
    const appUserId = loginResult.data.appUserId || loginResult.data.id
    if (!token || !appUserId) return null

    return {
      appUserId,
      token,
      username,
      createdAt: new Date().toISOString()
    }
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  // retry 2 lần => tổng 3 attempts
  // return { session, notRegistered }
  const loginWithRetry = async (retries, delayMs) => {
    const maxAttempts = (retries || 0) + 1
    let sawNotRegistered = false

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const loginResult = await appUserService.loginUserByPhone({
          phoneNumber: username,
          password: password
        })

        const session = buildSessionFromLoginResult(loginResult)
        if (session) return { session, notRegistered: false }

        // loginResult trả về không 200 nhưng có code
        if (loginResult?.error === 'PHONE_NUMBER_NOT_REGISTERED') {
          sawNotRegistered = true
          // có thể break sớm vì retry cũng vô nghĩa
          break
        }
      } catch (err) {}

      if (attempt < maxAttempts) {
        await sleep(delayMs || 300)
      }
    }

    return { session: null, notRegistered: sawNotRegistered }
  }

  // 1) Login trước
  const firstTry = await loginWithRetry(2, 300)
  if (firstTry.session) {
    saveUserSession(firstTry.session)
    return firstTry.session
  }

  // 2) Chỉ register nếu đúng lỗi PHONE_NUMBER_NOT_REGISTERED
  if (!firstTry.notRegistered) {
    return null
  }

  // 3) Register
  try {
    await appUserService.registerUserByPhone({
      phoneNumber: username,
      password: password
    })
  } catch (error) {
    // nếu race condition user vừa được tạo, vẫn login lại
  }

  // 4) Register xong -> Login lại
  const secondTry = await loginWithRetry(2, 300)
  if (secondTry.session) {
    saveUserSession(secondTry.session)
    return secondTry.session
  }

  return null
}

// Hàm lấy danh sách xe từ server
const fetchVehiclesFromServer = async (phone) => {
  if (!phone) return []

  try {
    const session = await ensureUserSessionByPhone(phone)
    if (!session?.token || !session?.appUserId) {
      return []
    }

    const result = await appUserService.getListVehicleByAppUserId(session.appUserId, session.token, 20)
    if (result.statusCode === 200) {
      const rawData = result.data?.data || result.data || []
      const mappedVehicles = rawData
        .map((item, index) => {
          const plate = item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || ''
          if (!plate) return null

          return {
            ...item,
            id: item.appUserVehicleId || index + 1,
            plate,
            vehicleType: item.vehicleType,
            createdAt: item.createdAt || item.appUserVehicleId // Fallback to ID if no createdAt
          }
        })
        .filter(Boolean)

      // Sort theo thứ tự mới nhất → cũ nhất (theo createdAt hoặc ID desc)
      const sortedVehicles = mappedVehicles.sort((a, b) => {
        const aTime = a.createdAt || a.id
        const bTime = b.createdAt || b.id
        return bTime - aTime // Descending order
      })

      return sortedVehicles
    } else {
      return []
    }
  } catch (error) {
    return []
  }
}

// Custom hook load lại khi phoneNumber thay đổi
export const useVehicleList = (phoneNumber) => {
  const [vehicles, setVehicles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const prevPhoneRef = useRef(phoneNumber)

  // Dùng phoneNumber từ parameter
  const phone = phoneNumber

  useEffect(() => {
    const loadVehicles = async () => {
      if (!phone) {
        setVehicles([])
        return
      }

      // Reset danh sách nếu phoneNumber thay đổi
      if (prevPhoneRef.current !== phone) {
        setVehicles([])
      }
      prevPhoneRef.current = phone

      setIsLoading(true)
      try {
        const vehicleList = await fetchVehiclesFromServer(phone)
        setVehicles(vehicleList)
      } catch (error) {
        setVehicles([])
      } finally {
        setIsLoading(false)
      }
    }
    loadVehicles()
  }, [phone]) // Re-fetch khi phone thay đổi

  // Function để thêm xe mới
  const addVehicleToList = async (plateNumber, vehicleTypeFromUserSelected, phoneNumber) => {
    try {
      // Đảm bảo có session user
      const session = await ensureUserSessionByPhone(phoneNumber)
      if (!session) {
        throw new Error('Không thể xác thực người dùng. Vui lòng thử lại sau.')
      }

      // Map vehicleType sang vehicleSubType
      const vehicleSubType = mapVehicleTypeToSubType(vehicleTypeFromUserSelected)

      // Gọi API register vehicle
      const result = await appUserService.registerVehicle(
        {
          vehicleIdentity: plateNumber,
          vehiclePlateColor: 'WHITE',
          vehicleSubType: vehicleSubType,
          vehicleType: vehicleTypeFromUserSelected
        },
        session.token
      )

      if (result.statusCode === 200) {
        // Refresh danh sách xe
        const updatedVehicles = await fetchVehiclesFromServer(phoneNumber)
        setVehicles(updatedVehicles)

        return { success: true, data: result.data }
      } else {
        return { success: false, message: result.message || 'Không thể thêm xe' }
      }
    } catch (error) {
      return { success: false, message: error.message || 'Có lỗi xảy ra khi thêm xe' }
    }
  }

  return { vehicles, isLoading, setVehicles, addVehicleToList }
}
